// Veilles enregistrées (spec § 11) : la liste filtrée devient une veille ; chaque nuit, le worker signale les fiches
// nouvelles qui y correspondent (alerte « saved_search »). La base fixe l'auteur et refuse une veille sans critère.
import { BellPlus, Trash2 } from "lucide-react";
import {
  useCreate,
  useDelete,
  useGetList,
  useListContext,
  useLocaleState,
  useNotify,
  useRefresh,
  useTranslate,
} from "ra-core";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import type { SavedSearch } from "../types";
import { categoryKey, countryName, phaseKey } from "./projectUtils";
import {
  criteriaFromFilters,
  hasCriterion,
  type SavedSearchCriteria,
} from "./savedSearchCriteria";

/** Libellés lisibles des critères d'une veille. */
const useCriteriaLabels = () => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const t = (key: string, options?: object) =>
    translate(`resources.projects.saved_searches.${key}`, options);
  return (criteria: SavedSearchCriteria) => [
    ...criteria.countries.map((code) => countryName(code, locale)),
    ...criteria.categories.map((category) => translate(categoryKey(category))),
    ...criteria.phases.map((phase) => translate(phaseKey(phase))),
    ...(criteria.min_score != null
      ? [t("min_score_label", { score: criteria.min_score })]
      : []),
    ...(criteria.min_budget_eur != null
      ? [
          t("min_budget_label", {
            amount: (criteria.min_budget_eur / 1_000_000).toLocaleString(
              locale,
            ),
          }),
        ]
      : []),
  ];
};

/** Bouton de la liste des projets : enregistre les filtres en cours comme veille. */
export const SaveSearchButton = ({ iconOnly }: { iconOnly?: boolean }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const { filterValues } = useListContext();
  const labels = useCriteriaLabels();
  const [create, { isPending }] = useCreate();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [minScore, setMinScore] = useState("");
  const [minBudget, setMinBudget] = useState("");
  const t = (key: string, options?: object) =>
    translate(`resources.projects.saved_searches.${key}`, options);

  const fromList = criteriaFromFilters(filterValues ?? {});
  const criteria: SavedSearchCriteria = {
    ...fromList,
    min_score: minScore ? Number(minScore) : null,
    min_budget_eur: minBudget ? Number(minBudget) * 1_000_000 : null,
  };
  const listLabels = labels({ ...fromList, min_score: null });

  const start = () => {
    setName(listLabels.join(" · "));
    setMinScore(fromList.min_score != null ? String(fromList.min_score) : "");
    setMinBudget("");
    setOpen(true);
  };

  const save = () =>
    create(
      "saved_searches",
      { data: { name: name.trim(), ...criteria } },
      {
        onSuccess: () => {
          setOpen(false);
          notify(t("saved"), { type: "success" });
        },
        onError: (error) =>
          notify(error instanceof Error ? error.message : String(error), {
            type: "error",
          }),
      },
    );

  const valid =
    name.trim() !== "" &&
    hasCriterion(criteria) &&
    (criteria.min_score == null ||
      (criteria.min_score >= 0 && criteria.min_score <= 100)) &&
    (criteria.min_budget_eur == null || criteria.min_budget_eur > 0);

  return (
    <>
      <Button
        variant={iconOnly ? "ghost" : "outline"}
        size={iconOnly ? "icon" : "sm"}
        className={iconOnly ? "rounded-full" : undefined}
        onClick={start}
        aria-label={t("save")}
      >
        <BellPlus className={iconOnly ? "size-5" : "size-4"} />
        {!iconOnly && t("save")}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              if (valid) save();
            }}
          >
            <DialogHeader>
              <DialogTitle>{t("save")}</DialogTitle>
              <DialogDescription>{t("dialog_intro")}</DialogDescription>
            </DialogHeader>
            <div className="flex flex-wrap gap-1.5">
              {listLabels.length ? (
                listLabels.map((label) => (
                  <Badge key={label} variant="secondary">
                    {label}
                  </Badge>
                ))
              ) : (
                <span className="text-sm text-muted-foreground">
                  {t("no_list_filter")}
                </span>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="saved-search-name">{t("name")}</Label>
              <Input
                id="saved-search-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                maxLength={120}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="saved-search-score">{t("min_score")}</Label>
                <Input
                  id="saved-search-score"
                  type="number"
                  min={0}
                  max={100}
                  inputMode="numeric"
                  value={minScore}
                  onChange={(event) => setMinScore(event.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="saved-search-budget">{t("min_budget")}</Label>
                <Input
                  id="saved-search-budget"
                  type="number"
                  min={0}
                  step="any"
                  inputMode="decimal"
                  value={minBudget}
                  onChange={(event) => setMinBudget(event.target.value)}
                />
              </div>
            </div>
            {!hasCriterion(criteria) && (
              <p className="text-sm text-muted-foreground">
                {t("needs_criterion")}
              </p>
            )}
            <DialogFooter>
              <Button type="submit" disabled={!valid || isPending}>
                {t("confirm")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

/** Veilles de l'utilisateur, en tête de l'écran des alertes. */
export const SavedSearchList = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const labels = useCriteriaLabels();
  const [deleteOne] = useDelete();
  const { data: searches } = useGetList<SavedSearch>("saved_searches", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "created_at", order: "DESC" },
  });
  const t = (key: string, options?: object) =>
    translate(`resources.projects.saved_searches.${key}`, options);

  return (
    <section className="flex flex-col gap-2" data-tour="saved-searches">
      <h3 className="text-base font-semibold">{t("title")}</h3>
      {!searches?.length ? (
        <p className="text-sm text-muted-foreground">{t("empty")}</p>
      ) : (
        <Card className="py-0">
          <CardContent className="px-0">
            <ul className="divide-y">
              {searches.map((search) => (
                <li
                  key={search.id}
                  className="flex items-start gap-3 px-4 py-3"
                >
                  <div className="flex-1 min-w-0 flex flex-col gap-1">
                    <span className="text-sm font-medium">{search.name}</span>
                    <div className="flex flex-wrap gap-1.5">
                      {labels(search).map((label) => (
                        <Badge key={label} variant="secondary">
                          {label}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t("delete")}
                    onClick={() =>
                      deleteOne(
                        "saved_searches",
                        { id: search.id, previousData: search },
                        {
                          // Les alertes de la veille sont effacées avec elle.
                          onSuccess: refresh,
                          onError: (error) =>
                            notify(
                              error instanceof Error
                                ? error.message
                                : String(error),
                              { type: "error" },
                            ),
                        },
                      )
                    }
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </section>
  );
};
